import ragService from '../services/ragService';
import { saveDocumentToFirestore, updateCaptureSessionMetadata } from '../services/firebase';
import { INTERVIEW_CONDUCT_RULES } from '../constants/interviewConduct';

export function messagesToTranscript(messages) {
    return (messages || [])
        .filter((m) => m.type === 'user' || m.type === 'ai')
        .map((m) => `${m.type === 'user' ? 'Expert' : 'Interviewer'}: ${m.content}`)
        .join('\n\n');
}

export function buildInterviewConversationHistory(template, messages, latestUserText = null) {
    const completion =
        template.completionCriteria ||
        'You have enough when the main topics are covered with specific, actionable detail.';
    const history = [
        {
            role: 'user',
            content:
                `[Capture interview — ${template.title}]\n\n` +
                `Goals:\n${template.systemPrompt}\n\n` +
                `Completion criteria (use ${'[CAPTURE_COMPLETE]'} only when these are met):\n${completion}\n` +
                INTERVIEW_CONDUCT_RULES
        },
        { role: 'assistant', content: template.openingMessage }
    ];
    for (const m of messages || []) {
        if (m.type === 'user' || m.type === 'ai') {
            history.push({
                role: m.type === 'user' ? 'user' : 'assistant',
                content: m.content
            });
        }
    }
    if (latestUserText) {
        history.push({ role: 'user', content: latestUserText });
    }
    return history;
}

export async function saveCaptureSessionToBrain({
    user,
    sessionId,
    messages,
    template,
    projectId,
    onDocumentCreated
}) {
    const transcript = messagesToTranscript(messages);
    if (!transcript.trim()) {
        throw new Error('Add at least one answer before saving to the knowledge base.');
    }

    const summaryResult = await ragService.generateResponse(
        `Summarize this knowledge capture interview in 2-4 short paragraphs. Include key facts, decisions, risks, and follow-up actions. Template: ${template.title}.`,
        user.uid,
        projectId || null,
        user.uid,
        [{ role: 'user', content: transcript.slice(0, 12000) }]
    );
    const summary = (summaryResult.response || '').trim();

    const fullText = `# ${template.title}\n\nCaptured: ${new Date().toLocaleString()}\n\n## Summary\n${summary}\n\n## Transcript\n${transcript}`;

    const newDoc = {
        name: `Capture: ${template.title}`,
        fileName: `Capture: ${template.title}`,
        content: summary.slice(0, 2000),
        fullText,
        size: fullText.length,
        type: 'Capture session',
        uploadDate: new Date().toISOString(),
        status: 'imported',
        projectId: projectId || 'default',
        source: { provider: 'captureSession', sessionId, templateId: template.id }
    };

    const saveResult = await saveDocumentToFirestore(newDoc);
    if (!saveResult.success) {
        throw new Error(saveResult.error || 'Could not save document');
    }

    newDoc.id = saveResult.documentId;
    await ragService.embedDocument(
        {
            id: newDoc.id,
            title: newDoc.name,
            content: fullText,
            type: 'document',
            source: 'captureSession',
            name: newDoc.name
        },
        user.uid,
        projectId || null
    );

    await updateCaptureSessionMetadata(user.uid, sessionId, {
        summary,
        documentId: saveResult.documentId,
        status: 'saved'
    });

    if (onDocumentCreated) {
        onDocumentCreated(newDoc);
    }

    return { summary, documentId: saveResult.documentId };
}
